/**
 * Pure H OS · Pre-Production Gap Closure · Cash Forecast (90-day rolling).
 * Simple projection derived from live burn (Cost Centers) plus a starting
 * cash balance. No historical import — a directional view that Iris (AI)
 * or the CEO can sanity check pre-Series-A.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, Button } from "../../components/lib";
import { DocumentActions } from "../../components/DocumentActions";
import { COST_CENTERS } from "../../mocks/costCentersData";
import { PRODUCTS_HUB } from "../../mocks/productsHubData";

export default function CashForecast() {
  const [startingCash, setStartingCash] = useState<number>(4_800_000);
  const [monthlyRevGrowthPct, setGrowth] = useState<number>(10);

  // Baseline monthly burn = current cost-center spent total (proxy for OpEx).
  const monthlyBurn  = useMemo(() => COST_CENTERS.reduce((s, c) => s + c.spentEGP, 0), []);
  const monthlyRev0  = useMemo(() => PRODUCTS_HUB.reduce((s, p) => s + p.monthlyRevenueEGP, 0), []);

  const months = useMemo(() => {
    let rev = monthlyRev0;
    let cash = startingCash;
    return Array.from({ length: 12 }).map((_, i) => {
      const label = new Date(2026, 6 + i, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
      const burn = monthlyBurn;
      const net = rev - burn;
      cash = cash + net;
      const row = { label, rev, burn, net, cash };
      rev = rev * (1 + monthlyRevGrowthPct / 100);
      return row;
    });
  }, [startingCash, monthlyRevGrowthPct, monthlyBurn, monthlyRev0]);

  const runwayMonths = useMemo(() => {
    let cash = startingCash;
    let n = 0;
    for (const m of months) {
      if (m.net >= 0) return "∞ (net-positive)";
      cash += m.net;
      n += 1;
      if (cash < 0) return `${n - 1} mo (cash out at ${m.label})`;
    }
    return `12+ mo (positive at horizon)`;
  }, [months, startingCash]);

  return (
    <div className="space-y-6" data-testid="cash-forecast-page">
      <Card testId="cf-summary">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <SectionHeader eyebrow="Cash Forecast · 12-month projection" title={`Runway estimate: ${runwayMonths}`} />
          <DocumentActions docKey="cash-forecast" testId="cf-doc-actions" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Starting cash (EGP)</div>
            <input
              type="number" value={startingCash}
              onChange={(e) => setStartingCash(Number(e.target.value) || 0)}
              data-testid="cf-input-cash"
              className="mt-1 w-full text-sm border rounded p-2 outline-none tabular-nums"
              style={{ borderColor: "var(--ph-border-subtle)", background: "var(--ph-surface)", color: "var(--ph-text)" }}
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>MoM revenue growth %</div>
            <input
              type="number" value={monthlyRevGrowthPct} step={0.5}
              onChange={(e) => setGrowth(Number(e.target.value) || 0)}
              data-testid="cf-input-growth"
              className="mt-1 w-full text-sm border rounded p-2 outline-none tabular-nums"
              style={{ borderColor: "var(--ph-border-subtle)", background: "var(--ph-surface)", color: "var(--ph-text)" }}
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Monthly OpEx (live)</div>
            <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {monthlyBurn.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Monthly revenue (live)</div>
            <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {monthlyRev0.toLocaleString()}</div>
          </div>
        </div>
        <div className="mt-4">
          <Button variant="ghost" onClick={() => { setStartingCash(4_800_000); setGrowth(10); }} testId="cf-reset">Reset assumptions</Button>
        </div>
      </Card>

      <Card padded={false} testId="cf-table">
        <div className="grid grid-cols-5 text-[10px] uppercase tracking-[0.14em] px-4 py-3" style={{ background: "var(--ph-surface-subtle)", color: "var(--ph-muted)" }}>
          <div>Month</div>
          <div className="text-right">Revenue</div>
          <div className="text-right">Burn</div>
          <div className="text-right">Net</div>
          <div className="text-right">End cash</div>
        </div>
        {months.map((m, i) => (
          <div key={i} data-testid={`cf-row-${i}`} className="grid grid-cols-5 text-sm px-4 py-2 items-center tabular-nums" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
            <div style={{ color: "var(--ph-text)" }}>{m.label}</div>
            <div className="text-right" style={{ color: "var(--ph-text)" }}>EGP {Math.round(m.rev).toLocaleString()}</div>
            <div className="text-right" style={{ color: "var(--ph-text)" }}>EGP {Math.round(m.burn).toLocaleString()}</div>
            <div className="text-right" style={{ color: m.net >= 0 ? "var(--ph-accent)" : "var(--ph-danger)" }}>{m.net >= 0 ? "+" : ""}EGP {Math.round(m.net).toLocaleString()}</div>
            <div className="text-right" style={{ color: m.cash >= 0 ? "var(--ph-text)" : "var(--ph-danger)" }}>EGP {Math.round(m.cash).toLocaleString()}</div>
          </div>
        ))}
      </Card>
    </div>
  );
}
